import { useState, useEffect } from 'react'
import {
  subscribeLessons,
  createLesson as createLessonService,
  updateLesson as updateLessonService,
} from '../services/lessonsService'
import { ymdLocal } from '../utils/datetime'
import type { Lesson, CreateLessonInput } from '../types/lesson'
import { deleteDoc, doc } from 'firebase/firestore'
import { db } from '../../firebase'

interface UseLessonsParams {
  teacherId: string
  tz: string
  range: {
    fromISOUtc: string
    toISOUtc: string
  }
}

export function useLessons({ teacherId, tz, range }: UseLessonsParams) {
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [byDay, setByDay] = useState<Map<string, Lesson[]>>(new Map())
  const [visible, setVisible] = useState<Lesson[]>([])

  // Subscribe to lessons
  useEffect(() => {
    if (!teacherId || !range.fromISOUtc || !range.toISOUtc) {
      console.log(
        '🔍 [STATE SET] Missing required params, skipping subscription',
      )
      return
    }
    console.log('🔍 [STATE SET] Subscribing with params:', {
      teacherId,
      from: range.fromISOUtc,
      to: range.toISOUtc,
      tz,
    })
    const unsubscribe = subscribeLessons({
      teacherId,
      from: range.fromISOUtc,
      to: range.toISOUtc,
      onData: (newLessons) => {
        console.log('🔍 [STATE SET] onData callback received:', {
          count: newLessons.length,
          lessons: newLessons.map((l) => ({
            id: l.id,
            startAt: l.startAt,
            startAtType: typeof l.startAt,
          })),
        })
        setLessons(newLessons)
        // Log state immediately after setting (this won't show the updated state due to React batching)
        console.log('🔍 [STATE SET] setLessons called with:', {
          count: newLessons.length,
          firstLesson: newLessons[0]
            ? {
                id: newLessons[0].id,
                startAt: newLessons[0].startAt,
                startAtType: typeof newLessons[0].startAt,
              }
            : null,
        })
        // Group by day
        const grouped = new Map<string, Lesson[]>()
        newLessons.forEach((lesson) => {
          const dateKey = ymdLocal(lesson.startAt, tz)
          console.log('🔍 [STATE SET] Grouping lesson:', {
            lessonId: lesson.id,
            startAt: lesson.startAt,
            timezone: tz,
            dateKey,
          })
          const existing = grouped.get(dateKey) || []
          grouped.set(dateKey, [...existing, lesson])
        })
        console.log('🔍 [STATE SET] Grouped by day:', {
          totalDays: grouped.size,
          days: Array.from(grouped.entries()).map(([key, lessons]) => ({
            date: key,
            count: lessons.length,
            lessonIds: lessons.map((l) => l.id),
          })),
        })
        setByDay(grouped)
        setVisible(newLessons)
      },
    })
    return () => {
      console.log('🔍 [STATE SET] Unsubscribing from lessons')
      unsubscribe()
    }
  }, [teacherId, range.fromISOUtc, range.toISOUtc, tz])

  // Log state changes
  useEffect(() => {
    console.log('🔍 [STATE SET] State updated:', {
      lessonsCount: lessons.length,
      byDaySize: byDay.size,
      visibleCount: visible.length,
      sampleLesson: lessons[0]
        ? {
            id: lessons[0].id,
            startAt: lessons[0].startAt,
            startAtType: typeof lessons[0].startAt,
          }
        : null,
    })
  }, [lessons, byDay, visible])

  // Create lesson
  const createLesson = async (input: CreateLessonInput): Promise<string> => {
    console.log('[useLessons] 🆕 Creating lesson:', input)
    const id = await createLessonService(input)
    console.log('[useLessons] ✅ Lesson created with ID:', id)
    return id
  }

  // Update lesson
  const updateLesson = async (
    lessonId: string,
    updates: Partial<Lesson>,
  ): Promise<void> => {
    console.log('[useLessons] 🔄 updateLesson called with:', {
      lessonId,
      teacherId,
      updates,
    })
    if (!teacherId) {
      throw new Error('Teacher ID is required to update lesson')
    }
    console.log('[useLessons] 🔄 Calling updateLessonService with:', {
      lessonId,
      teacherId,
      updates,
    })
    await updateLessonService(lessonId, teacherId, updates)
    console.log('[useLessons] ✅ updateLessonService completed')
  }

  // Delete lesson
  const deleteLesson = async (lessonId: string): Promise<void> => {
    console.log('[useLessons] 🗑️ Deleting lesson:', lessonId)
    if (!teacherId) {
      throw new Error('Teacher ID is required to delete lesson')
    }
    const teacherRef = doc(db, 'teachers', teacherId)
    const lessonRef = doc(teacherRef, 'calendar', lessonId)
    await deleteDoc(lessonRef)
    console.log('[useLessons] ✅ Lesson deleted')
  }

  return {
    lessons,
    byDay,
    visible,
    createLesson,
    updateLesson,
    deleteLesson,
  }
}
